import { Injectable } from '@angular/core';
import { clone, range } from 'lodash';

@Injectable({ providedIn: 'root' })
export class ArrayUtilities {
    static getClosestValue<T>(
        array: T[],
        comparedValue: T,
        // Returns a delta between two numbers
        compareToFunction: (arrayValue: T, comparedValue: T) => number
    ): T {
        let closestDifference = Number.POSITIVE_INFINITY;
        let closest = 0;
        for (let i = 0; i < array.length; i++) {
            const diff = compareToFunction(array[i], comparedValue);
            if (diff > closestDifference) {
                break;
            } else if (diff < closestDifference) {
                closest = i;
                closestDifference = diff;
            }
        }
        return array[closest];
    }

    // Generic Array Sort By
    arraySortBy<T>(arrayList: T[], fieldToSortBy: keyof T) {
        return arrayList
            .slice(0)
            .sort((a, b) =>
                a[fieldToSortBy] > b[fieldToSortBy]
                    ? 1
                    : a[fieldToSortBy] < b[fieldToSortBy]
                      ? -1
                      : 0
            );
    }

    // Polyfill from  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flat
    flat<T>(array: T[], depth?: number) {
        depth = depth === undefined ? 1 : Math.floor(depth);
        if (depth < 1) return Array.prototype.slice.call(array);
        return (function flat(arr, d) {
            // eslint-disable-next-line no-bitwise
            const len = arr.length >>> 0;
            let flattened: any = [];
            let i = 0;
            while (i < len) {
                if (arr[i] !== undefined) {
                    const el = arr[i];
                    if (Array.isArray(el) && d > 0)
                        flattened = flattened.concat(flat(el, d - 1));
                    else flattened.push(el);
                }
                i++;
            }
            return flattened;
        })(array, depth);
    }

    /**
     * Clones an array filled with objects by value. Thus the objects passed in
     * are passed *by value* in the returned array.
     */
    cloneObjectArray<T>(array: T[]): T[] {
        return array.map((item) => clone(item));
    }

    /**
     * Picks [count] unique values from the given array and returns them.
     *
     * @param array
     */
    pickRandomUniqueValues<T>(array: T[], count: number): T[] {
        if (count >= array.length) {
            throw new Error('Invalid count');
        }
        const picks = [];
        const possibleIndices = range(array.length);

        while (picks.length < count) {
            const index =
                possibleIndices[
                    Math.floor(Math.random() * possibleIndices.length)
                ];
            possibleIndices.splice(index, 1);
            picks.push(array[index]);
        }
        return picks;
    }

    remove<T>(array: T[], item: T) {
        const index = array.indexOf(item);
        if (index > -1) {
            array.splice(index, 1);
        }
    }

    areEqualArrays<T>(arr1: T[], arr2: T[]): boolean {
        const a = arr1.sort();
        const b = arr2.sort();
        return (
            Array.isArray(a) &&
            Array.isArray(b) &&
            a.length === b.length &&
            a.every((val, index) => val === b[index])
        );
    }

    shuffle<Type>(array: Type[]): Type[] {
        // https://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
        let currentIndex = array.length;

        // While there remain elements to shuffle...
        while (0 !== currentIndex) {
            // Pick a remaining element...
            const randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex--;

            // And swap it with the current element.
            [array[currentIndex], array[randomIndex]] = [
                array[randomIndex],
                array[currentIndex]
            ];
        }

        return array;
    }
}
